/**
 * Platform Admin · Launch Waitlist
 * Route: /platform/waitlist
 * Capability required: analytics:view
 *
 * Shows subscribers captured by the public Coming Soon gate:
 *   - Total + 24h / 7d signup KPIs
 *   - 30-day growth sparkline
 *   - Top referrer leaderboard
 *   - Sortable list w/ search
 *   - One-click CSV export
 */
import { useEffect, useMemo, useState } from "react";
import {
  Users, Trophy, Download, Search, Loader2, TrendingUp, Link2,
  RefreshCw,
} from "lucide-react";
import { apiGet, API } from "@/lib/api";

const bearer = () =>
  ({ Authorization: `Bearer ${localStorage.getItem("af_token") || ""}` });

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function KpiTile({ label, value, hint, tid }) {
  return (
    <div
      data-testid={tid}
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function Sparkline({ series }) {
  const cumulative = series.map((p) => p.cumulative);
  const max = Math.max(1, ...cumulative);
  const min = Math.min(...cumulative);
  const w = 480;
  const h = 80;
  const step = series.length > 1 ? w / (series.length - 1) : w;
  const path = cumulative
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / Math.max(1, max - min)) * (h - 8) - 4;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full h-20"
      data-testid="waitlist-growth-sparkline"
    >
      <defs>
        <linearGradient id="wl-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#10b981" stopOpacity="0.35" />
          <stop offset="1" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`${path} L ${w} ${h} L 0 ${h} Z`}
        fill="url(#wl-grad)"
      />
      <path d={path} stroke="#10b981" strokeWidth="2" fill="none" />
    </svg>
  );
}

export default function PlatformWaitlist() {
  const [list, setList] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    setError(null);
    try {
      const [wl, gr, lb] = await Promise.all([
        apiGet(`/v3/platform/waitlist${query ? `?q=${encodeURIComponent(query)}` : ""}`),
        apiGet(`/v3/platform/waitlist/growth?days=30`),
        apiGet(`/v3/platform/waitlist/leaderboard?limit=10`),
      ]);
      setList(wl);
      setGrowth(gr);
      setLeaderboard(lb);
    } catch (e) {
      setError(e.message || "Failed to load waitlist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  const exportCsv = () => {
    // CSV endpoint returns a file; download via fetch + blob to preserve auth
    fetch(`${API}/v3/platform/waitlist/export`, { headers: bearer() })
      .then((r) => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message));
  };

  const stats = list?.stats;
  const items = list?.items || [];

  const growthDelta = useMemo(() => {
    if (!growth?.series?.length) return null;
    const s = growth.series;
    const first = s[0]?.cumulative || 0;
    const last = s[s.length - 1]?.cumulative || 0;
    return last - first;
  }, [growth]);

  return (
    <div data-testid="platform-waitlist-page" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-600" /> Launch Waitlist
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Pre-launch signups captured by the Coming Soon gate. Referral
            tracking + CSV export.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => load(q.trim())}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors"
            data-testid="waitlist-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            data-testid="waitlist-export-csv"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 text-sm font-semibold transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div
          data-testid="waitlist-error"
          className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
        >
          {error}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiTile
          tid="waitlist-kpi-total"
          label="Total signups"
          value={stats?.total?.toLocaleString() ?? "—"}
          hint="All-time"
        />
        <KpiTile
          tid="waitlist-kpi-24h"
          label="Last 24h"
          value={stats?.signups_24h?.toLocaleString() ?? "—"}
        />
        <KpiTile
          tid="waitlist-kpi-7d"
          label="Last 7d"
          value={stats?.signups_7d?.toLocaleString() ?? "—"}
        />
        <KpiTile
          tid="waitlist-kpi-referral"
          label="Referral signups"
          value={stats?.referral_signups?.toLocaleString() ?? "—"}
          hint="Referred by a friend"
        />
        <KpiTile
          tid="waitlist-kpi-viral"
          label="Viral coefficient"
          value={stats ? `${stats.viral_coefficient_pct}%` : "—"}
          hint="% brought by referral"
        />
      </div>

      {/* Growth + Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div
          className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4"
          data-testid="waitlist-growth-card"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                30-day growth
              </div>
              <div className="text-xs text-slate-500">
                Cumulative subscribers over time
              </div>
            </div>
            {growthDelta !== null && (
              <div className="text-sm font-bold text-emerald-600 tabular-nums">
                +{growthDelta.toLocaleString()} in 30d
              </div>
            )}
          </div>
          <div className="mt-3">
            {growth?.series?.length ? (
              <Sparkline series={growth.series} />
            ) : (
              <div className="h-20 flex items-center justify-center text-xs text-slate-400">
                No data yet
              </div>
            )}
          </div>
        </div>

        <div
          className="rounded-xl border border-slate-200 bg-white p-4"
          data-testid="waitlist-leaderboard-card"
        >
          <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
            <Trophy className="w-4 h-4 text-amber-500" /> Top referrers
          </div>
          <div className="mt-3 divide-y divide-slate-100">
            {(leaderboard?.items || []).length === 0 && (
              <div className="text-xs text-slate-400 py-3">
                No referrals yet — first one gets a mention.
              </div>
            )}
            {(leaderboard?.items || []).map((r, i) => (
              <div
                key={r.referral_code}
                className="flex items-center justify-between py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">
                    #{i + 1} {r.name || r.email}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">
                    {r.referral_code}
                  </div>
                </div>
                <div className="ml-2 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2 py-0.5 tabular-nums">
                  {r.referrals} ref
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Search + Table */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <form onSubmit={onSearch} className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="waitlist-search"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, name or code…"
              className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
            />
          </form>
          <div className="text-xs text-slate-500 tabular-nums">
            Showing {items.length} rows
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2 font-bold">Email</th>
                <th className="px-4 py-2 font-bold">Name</th>
                <th className="px-4 py-2 font-bold">Code</th>
                <th className="px-4 py-2 font-bold">Referred by</th>
                <th className="px-4 py-2 font-bold text-right">Referrals</th>
                <th className="px-4 py-2 font-bold">Source</th>
                <th className="px-4 py-2 font-bold">Joined</th>
              </tr>
            </thead>
            <tbody data-testid="waitlist-table-body">
              {loading && !list && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin inline-block" />
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">
                    No subscribers yet.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`waitlist-row-${it.referral_code}`}
                  className="border-t border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-4 py-2 font-mono text-xs text-slate-800">
                    {it.email}
                  </td>
                  <td className="px-4 py-2 text-slate-700">
                    {it.name || "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-emerald-700">
                    {it.referral_code}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">
                    {it.referred_by ? (
                      <span className="inline-flex items-center gap-1">
                        <Link2 className="w-3 h-3" /> {it.referred_by}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-slate-900">
                    {it.referral_count || 0}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {it.source || "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {fmtDate(it.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
